import {
  Box,
  Button,
  FieldSelect,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { hasFeature, IfFeatureFlagOn } from 'components/shared/featureFlag';
import { getRequestID } from 'components/shared/util/request';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import { FlowCallNodeForward, PhoneNumber } from '../nodeTypes/forward';
import { CallPattern } from '../types';

import ForwardTable from './forward/forwardTable';
import { TIMEOUT_OPTIONS } from './forward/forwardTableRow';
import styles from './forward.module.scss';

type StateProps = ReturnType<typeof mapStateToProps>;
type Props = FlowManagerEditModalComponentT<FlowCallNodeForward> & StateProps;

const CALL_PATTERN_OPTIONS = [
  { id: CallPattern.FirstToPickUp, name: 'First to pick up' },
  { id: CallPattern.Sequence, name: 'Sequence' },
];

const DISPLAY_NUMBER_OPTIONS = [
  { id: 'b_number', name: 'The called number' },
  { id: 'a_number', name: "The caller's number" },
];

const getEmptyNumber = (): PhoneNumber => ({
  countryCode: null,
  id: getRequestID(),
  isValid: false,
  phoneNumber: null,
  sip: null,
  timeout: '20',
  type: 'phone',
  user: null,
});

const ForwardModalImpl: FC<Props> = ({
  triggerClose,
  replaceNode,
  node,
  nodeLabel,
  hasACforward,
}) => {
  const { payload } = node;
  const initForward = useMemo(() => {
    const numbers = !payload?.numbers?.length
      ? [getEmptyNumber()]
      : payload.numbers.map(({ timeout, isValid, ...number }) => {
          const [{ id: firstTimeoutOption }] = TIMEOUT_OPTIONS;
          const isTimeoutValid = TIMEOUT_OPTIONS.some(
            ({ id }) => id === timeout,
          );

          return {
            ...number,
            /*
             * This is for backwards compatibility.
             * Since the "No timeout" (null) option was actually 10 minute option,
             * we want to update the values to accurately reflect that.
             */
            timeout: timeout ?? firstTimeoutOption,
            isValid: isTimeoutValid && isValid,
          };
        });

    if (!payload) {
      return {
        callPattern: CallPattern.FirstToPickUp,
        display_number: 'b_number',
        noAnswerNodeId: null,
        numbers,
        recordCall: true,
      };
    }

    const display_number =
      !payload.display_number || !hasACforward
        ? 'b_number'
        : payload.display_number;

    return {
      ...payload,
      display_number,
      numbers,
    };
  }, [payload, hasACforward]);

  const [error, setError] = useState<null | string>(null);
  const [forward, setForward] = useState(initForward);
  const isChanged = useIsChanged([initForward, forward]);

  const onCallPatternChange = useCallback(
    (callPattern) => {
      setForward((forward) => {
        const state = { ...forward, callPattern };
        const isCallFirstToPickUp = callPattern === CallPattern.FirstToPickUp;

        if (!isCallFirstToPickUp) {
          return state;
        }

        const { numbers: stateNumbers } = state;
        const [{ timeout: firstNumberTimeout }] = stateNumbers;
        const numbers = stateNumbers.map((number) => ({
          ...number,
          timeout: firstNumberTimeout,
        }));

        return {
          ...state,
          numbers,
        };
      });
    },
    [setForward],
  );
  const onDisplayNumberChange = useCallback(
    (display_number) => {
      setForward((forward) => ({
        ...forward,
        display_number,
      }));
    },
    [setForward],
  );
  const onUpdateNumbers = useCallback(
    (numbers) => {
      setForward((forward) => ({
        ...forward,
        numbers,
      }));
    },
    [setForward],
  );
  const onAddNumber = useCallback(() => {
    setForward((forward) => ({
      ...forward,
      numbers: [...forward.numbers, getEmptyNumber()],
    }));
  }, [setForward]);
  const onSave = useCallback(() => {
    // TODO: Check if all numbers are valid

    const { numbers, noAnswerNodeId, callPattern, display_number } = forward;
    const isSomeNumberInvalid = numbers.some((number) => !number.isValid);

    if (isSomeNumberInvalid) {
      setError('One or more of the numbers are invalid.');

      return;
    }

    if (
      callPattern === CallPattern.FirstToPickUp &&
      display_number === 'a_number'
    ) {
      setError(
        `The telephony networks prevent us from using "The caller's number" in the "First to pick up" pattern.`,
      );
      return;
    }

    const numberWithoutTimeout = numbers.some((number) => !number.timeout);

    const payload = {
      ...forward,
      noAnswerNodeId: numberWithoutTimeout ? null : noAnswerNodeId || uuid(),
    };

    setError(null);
    replaceNode({
      ...node,
      icon: 'i-forward-to-phone',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.FORWARD,
      text: 'forward',
      payload,
    });

    triggerClose();
  }, [forward, node, setError, replaceNode, triggerClose]);

  const { callPattern = '', display_number = '' } = forward;

  return (
    <Box width="600px">
      <ModalHead title={nodeLabel} onClose={triggerClose} />
      <ModalBody>
        <Text.Paragraph subdued>
          Set up incoming calls to be forwarded to one or more phone numbers.
          The numbers can belong to your company (internal) or other companies
          (external).
        </Text.Paragraph>
        <FormGrid className={styles.form}>
          <FormField>
            <FieldSelect
              label="Call pattern"
              input={{
                name: 'callPatternSelect',
                value: callPattern,
                onChange: onCallPatternChange,
              }}
              items={CALL_PATTERN_OPTIONS}
              helpIcon={{
                title: 'Forward call pattern',
                text: (
                  <>
                    Set the call pattern to <b>first agent to pick</b> up or{' '}
                    <b>sequence</b> (i.e. the first number on the list is called
                    and if the call is not answered, then the second, and so on)
                  </>
                ),
              }}
              required
            />
          </FormField>
          <IfFeatureFlagOn name="a-c-forward">
            <FormField>
              <FieldSelect
                label="Display number"
                input={{
                  name: 'displayNumberSelect',
                  value: display_number,
                  onChange: onDisplayNumberChange,
                }}
                items={DISPLAY_NUMBER_OPTIONS}
                helpIcon={{
                  title: 'Display number',
                  text: `Select what you want the show as Caller ID. You can choose to show the caller’s number or the called number. This number will be presented to the recipient when activating the Forward Call action.`,
                }}
                required
              />
            </FormField>
          </IfFeatureFlagOn>
          <FormField>
            <ForwardTable forward={forward} onUpdateNumbers={onUpdateNumbers} />
          </FormField>
          <FormField span={2}>
            <Button variant="secondary" onClick={onAddNumber}>
              Add number
            </Button>
          </FormField>
        </FormGrid>
        {error && <p className={styles.error}>{error}</p>}
      </ModalBody>
      <ModalFoot
        confirmOnClick={onSave}
        cancelOnClick={triggerClose}
        confirmProps={{ disabled: !isChanged }}
        confirmName={getConfirmButtonVerb(node)}
      />
    </Box>
  );
};

const mapStateToProps = (state: RootReducerStateT) => ({
  hasACforward: hasFeature(state, 'a-c-forward'),
});

export const ForwardModal = connect(mapStateToProps)(ForwardModalImpl);
